import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Lock, ScrollText, Info, GraduationCap, BookOpen, NotebookPen } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import DOMPurify from 'dompurify';
import ProgrammeRubricScore from '../components/programme/ProgrammeRubricScore';
import StudyNotesCard, { useMyStudyNotes } from '../components/programme/StudyNotesCard';
import LearnerActivityRow from '../components/programme/LearnerActivityRow';
import LearnerWeekCard from '../components/programme/LearnerWeekCard';
import { activityLaunchPath } from '../lib/activityTypes';
import ResumeCard from '../components/programme/ResumeCard';
import ModuleReflectionCard from '../components/programme/ModuleReflectionCard';
import WeekAskNavi from '../components/programme/WeekAskNavi';
import ProgrammeOutlineCard from '../components/programme/ProgrammeOutlineCard';
import LearnerModuleOverview from '../components/programme/LearnerModuleOverview';
import { weekColor } from '../components/admin/WeekNavigator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import InfoPanel from '../components/programme/InfoPanel';
import { mediaUrl } from '../lib/mediaUrl';
import useProgrammeUiState from '../hooks/useProgrammeUiState';
import AskInstructorCard from '../components/programme/AskInstructorCard';

const API = `${API_BASE}/api`;


export default function ProgrammeDetailPage() {
  const navigate = useNavigate();
  const { programmeId } = useParams();
  const { token, isAuthenticated, user } = useAuth();

  const headers = { Authorization: `Bearer ${token}` };

  const [programme, setProgramme] = useState(null);
  const [progress, setProgress] = useState(null);
  const [modules, setModules] = useState([]);
  const studyNotes = useMyStudyNotes(programmeId, token);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/programmes/${programmeId}/modules`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setModules(r.data.modules || []))
      .catch(() => setModules([]));
  }, [programmeId, token]);

  useEffect(() => {
    if (!modules.length || !window.location.hash.startsWith('#module-')) return;
    const t = setTimeout(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
    return () => clearTimeout(t);
  }, [modules]);
  const [loading, setLoading] = useState(true);
  const [storedUi, saveUi] = useProgrammeUiState(user?.user_id || user?.email, programmeId);
  const [uploadingActivity, setUploadingActivity] = useState(null);

  useEffect(() => {
    fetchProgramme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId, token, isAuthenticated]);

  const fetchProgramme = async () => {
    try {
      const [progRes, progressRes] = await Promise.all([
        axios.get(`${API}/programmes/${programmeId}`, { headers }),
        isAuthenticated 
          ? axios.get(`${API}/programmes/${programmeId}/my-progress`, { headers }).catch(() => ({ data: null }))
          : Promise.resolve({ data: null })
      ]);
      
      setProgramme(progRes.data);
      setProgress(progressRes.data);

    } catch (error) {
      toast.error(error.response?.status === 403 ? (error.response.data?.detail || "You're not enrolled in this programme") : 'Failed to load programme');
      navigate('/programmes');
    } finally {
      setLoading(false);
    }
  };

  const isActivityComplete = (activityId) => {
    return progress?.completed_activity_ids?.includes(activityId);
  };

  const getPoeSubmission = (activityId) => {
    return progress?.poe_submissions?.find(s => s.activity_id === activityId);
  };

  // Simple content (video, document, masterclass) counts as complete once the learner opens it
  const recordOpened = async (activity) => {
    if (!progress?.enrollment || isActivityComplete(activity.activity_id) || activity.allows_poe_submission) return;
    try {
      await axios.post(`${API}/programmes/${programmeId}/activities/${activity.activity_id}/complete`, {}, { headers });
      fetchProgramme();
    } catch (error) {
      console.warn('Could not record completion', error);
    }
  };

  const handlePoeUpload = async (activityId, file) => {
    if (!file) return;

    const allowedTypes = ['.pdf', '.ppt', '.pptx'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedTypes.includes(fileExt)) {
      toast.error('Only PDF and PowerPoint files are allowed');
      return;
    }

    setUploadingActivity(activityId);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API}/programmes/${programmeId}/activities/${activityId}/submit-poe`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Portfolio of Evidence submitted!');
      fetchProgramme();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit';
      toast.error(message);
    } finally {
      setUploadingActivity(null);
    }
  };

  const launchActivity = async (activity) => {
    const isLab = activity.content_type === 'learning_lab' || ['learning_lab', 'ai_learning_lab', 'existing_lab'].includes(activity.activity_type);
    if (isLab) {
      // Same learner player as the Learning Lab menu
      if (activity.content_id) navigate(activityLaunchPath(activity, programmeId));
      return;
    }
    switch (activity.activity_type) {
      case 'assignment':
        navigate(`/programmes/${programmeId}/assignments/${activity.activity_id}`);
        break;
      case 'course':
      case 'ai_course':
      case 'existing_course':
        if (activity.ai_course_id || activity.content_id) {
          navigate(activityLaunchPath(activity, programmeId));
        }
        break;
      case 'masterclass':
        if (activity.content_id) {
          await recordOpened(activity);
          navigate(`/masterclasses/${activity.content_id}`);
        }
        break;
      case 'aicc':
      case 'video':
        // Open iframe content in a modal or new tab
        if (activity.external_url) {
          window.open(activity.external_url, '_blank');
          recordOpened(activity);
        }
        break;
      case 'document':
      case 'assignment_brief':
        // Download document from the programme workflow endpoint
        if (activity.document_id || activity.document_filename) {
          try {
            const response = await axios.get(
              `${API}/programmes/${programmeId}/activities/${activity.activity_id}/download-document`,
              { headers, responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', activity.document_filename || 'document.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Document downloaded');
            recordOpened(activity);
          } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download document');
          }
        } else if (activity.document_url) {
          window.open(`${API_BASE}${activity.document_url}`, '_blank');
          recordOpened(activity);
        }
        break;
      default:
        break;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-zinc-800 rounded w-1/3" />
            <div className="h-64 bg-zinc-800 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!programme) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 py-8 text-center">
          <p className="text-zinc-400">Programme not found</p>
        </div>
      </Layout>
    );
  }

  const isEnrolled = progress?.enrollment !== undefined;
  const resetPreview = async () => {
    if (!window.confirm('Reset your preview? This clears your completed activities, reflections, assignment uploads and course/lab completions for this programme so you can start again as a new learner.')) return;
    try {
      await axios.post(`${API}/programmes/${programmeId}/my-preview/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Preview reset — you are starting fresh');
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reset preview');
    }
  };
  const visibleActivities = (programme.periods || []).flatMap((p) => p.activities || []).filter((a) => a.status !== 'draft');
  const completedIds = new Set(progress?.completed_activity_ids || []);
  const completedCount = visibleActivities.filter((a) => completedIds.has(a.activity_id)).length;
  const overallProgress = visibleActivities.length ? Math.round((completedCount / visibleActivities.length) * 100) : 0;

  const notesModules = new Set(studyNotes.map((n) => n.module_id));
  const periodTitle = Object.fromEntries((programme.periods || []).map((p) => [p.period_id, p.title]));
  const nextActivity = isEnrolled ? visibleActivities.find((a) => !a.locked && !completedIds.has(a.activity_id)) : null;
  const nextActivityId = nextActivity?.activity_id;
  const moduleStats = (moduleId) => {
    const ps = (programme.periods || []).filter((p) => p.module_id === moduleId);
    const acts = ps.flatMap((p) => (p.activities || []).filter((a) => a.status !== 'draft'));
    return { weeks: ps.length, total: acts.length, completed: acts.filter((a) => completedIds.has(a.activity_id)).length };
  };
  const allPeriods = programme.periods || [];
  const currentPeriod = allPeriods.find((p) => p.period_id === nextActivity?.period_id) || allPeriods.find((p) => !p.locked) || allPeriods[0];
  const hashModule = window.location.hash.startsWith('#module-') ? window.location.hash.slice(8) : null;
  const defaults = { tab: 'learning', modules: { [hashModule || currentPeriod?.module_id || 'none']: true }, periods: currentPeriod ? { [currentPeriod.period_id]: true } : {}, panels: {} };
  const ui = { ...defaults, ...(storedUi || {}) };
  const toggleUi = (group, id) => saveUi({ ...ui, [group]: { ...ui[group], [id]: !ui[group]?.[id] } });
  const groups = [];
  allPeriods.forEach((period, index) => {
    const key = modules.some((m) => m.module_id === period.module_id) ? period.module_id : 'none';
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push({ period, index });
    else groups.push({ key, moduleIdx: modules.findIndex((m) => m.module_id === key), items: [{ period, index }] });
  });
  const programmeInfo = [['Facilitator', programme.facilitator_name], ['Moderator', programme.moderator_name],
    ['Start Date', programme.start_date && formatDate(programme.start_date)], ['End Date', programme.end_date && formatDate(programme.end_date)]].filter(([, v]) => v);
  const infoChips = [
    ['Professional Body', programme.professional_body], ['NQF Level', programme.nqf_level],
    ['Duration', programme.duration], ['Credits', programme.credits],
  ].filter(([, v]) => v);

  return (
    <Layout pageTitle={programme.programme_name || 'Programme'}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" size="sm" onClick={() => navigate('/programmes')} className="text-zinc-400 hover:text-white mb-6 -ml-2" data-testid="back-to-programmes-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />Back to Programmes
        </Button>

        {/* Programme Hero */}
        <section className="relative overflow-hidden rounded-3xl border border-zinc-800 bg-zinc-900 p-6 sm:p-8 mb-10" data-testid="programme-hero">
          {programme.thumbnail_url && (
            <>
              <img src={mediaUrl(programme.thumbnail_url)} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" data-testid="programme-hero-image" />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/40" />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-zinc-950/90 via-transparent to-transparent" />
            </>
          )}
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_100%_0%,rgba(20,184,166,0.14),transparent_40%)]" />
          <div className="relative flex flex-col lg:flex-row lg:items-end gap-8">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-400 mb-3">{programme.structure_type === 'months' ? 'Monthly' : 'Weekly'} Programme</p>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-3" data-testid="programme-title">{programme.programme_name || 'Professional Programme'}</h1>
              {programme.description && <p className="text-zinc-400 max-w-2xl leading-relaxed line-clamp-3">{programme.description}</p>}
              {infoChips.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-5">
                  {infoChips.map(([k, v]) => (
                    <span key={k} className="px-3 py-1.5 rounded-full bg-zinc-800/80 border border-zinc-700/60 text-xs text-zinc-300"><span className="text-zinc-500">{k}:</span> {v}</span>
                  ))}
                </div>
              )}
            </div>
            {isEnrolled && (
              <div className="lg:w-64 shrink-0 rounded-2xl bg-zinc-950/60 border border-zinc-800 p-5" data-testid="programme-progress-panel">
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">Your progress</p>
                <p className="text-4xl font-bold text-white mt-1" data-testid="programme-progress-pct">{overallProgress}<span className="text-xl text-teal-400">%</span></p>
                <Progress value={overallProgress} className="h-2 bg-zinc-800 mt-3" />
                <p className="text-xs text-zinc-500 mt-2">{completedCount} of {visibleActivities.length} activities complete</p>
              </div>
            )}
          </div>
          {isEnrolled && (
            <div className="relative mt-6">
              <ResumeCard programmeId={programmeId} started={completedCount > 0} onLaunch={launchActivity}
                activity={nextActivity ? { ...nextActivity, period_title: periodTitle[nextActivity.period_id] } : null} />
            </div>
          )}
        </section>

        {progress?.enrollment?.is_admin_preview && (
          <div className="flex items-center gap-3 rounded-xl bg-teal-500/10 border border-teal-500/30 p-4 mb-8" data-testid="admin-preview-banner">
            <Lock className="h-5 w-5 text-teal-300" />
            <p className="text-teal-100 text-sm flex-1">Admin preview — you are enrolled as a preview learner. Your activity is not counted in learner stats, the gradebook or at-risk alerts.</p>
            <Button size="sm" variant="outline" onClick={resetPreview} className="border-teal-500/50 text-teal-200 hover:bg-teal-500/15 shrink-0" data-testid="reset-preview-btn">Reset my preview</Button>
          </div>
        )}

        {isEnrolled && !progress?.enrollment?.is_admin_preview && <AskInstructorCard programmeId={programmeId} token={token} />}

        {user?.role === 'instructor' && !isEnrolled && (
          <div className="flex items-center gap-3 rounded-xl bg-teal-500/10 border border-teal-500/30 p-4 mb-8" data-testid="instructor-view-banner">
            <Lock className="h-5 w-5 text-teal-300" />
            <p className="text-teal-100 text-sm flex-1">Instructor view: you're seeing this programme as its instructor.</p>
            <Button size="sm" variant="outline" onClick={() => navigate(`/instructor/programmes/${programmeId}`)} className="border-teal-500/50 text-teal-200 hover:bg-teal-500/15 shrink-0" data-testid="open-instructor-hub-btn">Open Instructor Hub</Button>
          </div>
        )}

        {isAuthenticated && !isEnrolled && user?.role !== 'instructor' && (
          <div className="flex items-center gap-3 rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 mb-8" data-testid="not-enrolled-banner">
            <Lock className="h-5 w-5 text-amber-400" />
            <p className="text-amber-200 text-sm">You are not enrolled in this programme. Contact your administrator to request enrolment.</p>
          </div>
        )}

        <Tabs value={ui.tab} onValueChange={(tab) => saveUi({ ...ui, tab })} data-testid="programme-tabs">
          <TabsList className="bg-zinc-900 border border-zinc-800 rounded-2xl sm:rounded-full p-1 h-auto mb-6 grid grid-cols-3 w-full sm:w-auto sm:inline-flex">
            <TabsTrigger value="learning" className="rounded-xl sm:rounded-full px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-normal leading-tight data-[state=active]:bg-teal-500 data-[state=active]:text-black" data-testid="tab-my-learning"><BookOpen className="hidden sm:block h-4 w-4 mr-2" />My Learning</TabsTrigger>
            <TabsTrigger value="about" className="rounded-xl sm:rounded-full px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-normal leading-tight data-[state=active]:bg-teal-500 data-[state=active]:text-black" data-testid="tab-about-programme"><Info className="hidden sm:block h-4 w-4 mr-2" />About this programme</TabsTrigger>
            {isEnrolled && <TabsTrigger value="notes" className="rounded-xl sm:rounded-full px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-normal leading-tight data-[state=active]:bg-teal-500 data-[state=active]:text-black" data-testid="tab-notes-grades"><GraduationCap className="hidden sm:block h-4 w-4 mr-2" />Notes &amp; Grades</TabsTrigger>}
          </TabsList>

          <TabsContent value="learning" className="space-y-4 mt-0" data-testid="tab-content-learning">
            {allPeriods.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="py-12 text-center">
                  <Calendar className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">No content has been added to this programme yet.</p>
                </CardContent>
              </Card>
            ) : groups.map((group) => {
              const module = group.moduleIdx >= 0 ? modules[group.moduleIdx] : null;
              const color = weekColor(group.moduleIdx >= 0 ? group.moduleIdx : 0);
              const weeks = group.items.map(({ period, index }) => {
                const periodActivities = (period.activities || []).filter((a) => a.status !== 'draft');
                return (
                  <LearnerWeekCard key={period.period_id} period={period} index={index} color={color} open={!!ui.periods[period.period_id]} onToggle={() => toggleUi('periods', period.period_id)}
                    completed={periodActivities.filter((a) => isActivityComplete(a.activity_id)).length} total={periodActivities.length} isEnrolled={isEnrolled}>
                    {periodActivities.length === 0 && <p className="text-sm text-zinc-500">No activities released yet.</p>}
                    {periodActivities.map((activity) => (
                      <LearnerActivityRow key={activity.activity_id} activity={activity} isEnrolled={isEnrolled}
                        isComplete={isActivityComplete(activity.activity_id)} isNext={activity.activity_id === nextActivityId}
                        poeSubmission={getPoeSubmission(activity.activity_id)} uploading={uploadingActivity === activity.activity_id}
                        onLaunch={launchActivity} onPoeUpload={handlePoeUpload} />
                    ))}
                    {isEnrolled && periodActivities.length > 0 && <WeekAskNavi programmeId={programmeId} period={period} token={token} />}
                  </LearnerWeekCard>
                );
              });
              if (!module) return <div key={`none-${group.items[0].period.period_id}`} className="space-y-3">{weeks}</div>;
              const stats = moduleStats(module.module_id);
              return (
                <LearnerModuleOverview key={module.module_id} module={module} color={color} completed={stats.completed} total={isEnrolled ? stats.total : 0} weekCount={stats.weeks}
                  hasNotes={notesModules.has(module.module_id)} programmeId={programmeId} token={token}
                  expanded={!!ui.modules[module.module_id]} onToggleExpand={() => toggleUi('modules', module.module_id)}>
                  {weeks}
                  {isEnrolled && !module.locked && <ModuleReflectionCard programmeId={programmeId} module={module} token={token} color={color} />}
                </LearnerModuleOverview>
              );
            })}
          </TabsContent>

          <TabsContent value="about" className="mt-0" data-testid="tab-content-about">
            {programme.instructions_html && (
              <InfoPanel testId="programme-instructions" title="Programme Instructions" icon={ScrollText} open={ui.panels.instructions} onToggle={() => toggleUi('panels', 'instructions')}>
                <div className="rich-content text-zinc-300 pt-3" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(programme.instructions_html) }} />
              </InfoPanel>
            )}
            {isEnrolled && <ProgrammeOutlineCard programmeId={programmeId} token={token} open={ui.panels.outline} onToggle={() => toggleUi('panels', 'outline')} />}
            {programmeInfo.length > 0 && (
              <InfoPanel testId="programme-information" title="Programme Information" icon={Info} open={ui.panels.info} onToggle={() => toggleUi('panels', 'info')}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3">
                  {programmeInfo.map(([k, v]) => (
                    <div key={k}><p className="text-xs text-zinc-500 uppercase tracking-wide">{k}</p><p className="text-white mt-1">{v}</p></div>
                  ))}
                </div>
              </InfoPanel>
            )}
            {!programme.instructions_html && !isEnrolled && programmeInfo.length === 0 && <p className="text-sm text-zinc-500" data-testid="about-empty">No programme details have been added yet.</p>}
          </TabsContent>

          {isEnrolled && (
            <TabsContent value="notes" className="mt-0" data-testid="tab-content-notes">
              <StudyNotesCard programmeId={programmeId} token={token} notes={studyNotes} open={ui.panels.notes} onToggle={() => toggleUi('panels', 'notes')} />
              <ProgrammeRubricScore programmeId={programmeId} token={token} refreshKey={progress?.completed_activity_ids?.length}
                open={ui.panels.rubric} onToggle={() => toggleUi('panels', 'rubric')}
                onEmpty={!studyNotes.length ? <div className="rounded-2xl border border-dashed border-zinc-800 p-10 text-center" data-testid="notes-grades-empty"><NotebookPen className="h-8 w-8 text-zinc-600 mx-auto mb-3" /><p className="text-zinc-400 text-sm">Study notes and your assessment score will appear here once they are released.</p></div> : null} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}
